import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import JSZip from 'jszip';
import './ModelSettingsPage.css';
import { CatalogFeedback } from './ModelSettingsCatalogFeedback';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import FenceLinkedSources from '../components/FenceLinkedSources';
import { Dialog } from '../components/Dialog';
import type {
  AiFenceModelCatalogItem,
  AuthMode,
  AuthType,
  MeResponse,
  ModelAuth,
  ModelConfig,
  SupervisionSettings,
  ProviderKey,
  ScriptPlatform,
  SubscriptionVariant,
  UsageProvider,
  UsageProviderKind
} from '../types';
import { reportError, reportErrorLocal } from '../utils/errors';

const providerAllowsAuth = (
  provider: string,
  authType: string,
  authProvider?: string | null
) => {
  const normalized = provider.toLowerCase();
  const normalizedAuthProvider = (authProvider ?? '').toLowerCase();
  const effectiveProvider =
    normalized === 'openai-compatible' || normalized === 'oss' ? 'codex' : normalized;
  if (normalizedAuthProvider && normalizedAuthProvider !== effectiveProvider) {
    return false;
  }
  if (effectiveProvider === 'claude' || effectiveProvider === 'claude-code') {
    return ['api_key', 'login_bundle'].includes(authType);
  }
  if (effectiveProvider === 'junie' || effectiveProvider === 'junie-cli') {
    return ['api_key', 'tokens'].includes(authType);
  }
  return ['api_key', 'login_bundle', 'tokens'].includes(authType);
};

const authTypeLabel = (authType: string, provider?: string | null) => {
  const normalized = (provider ?? '').toLowerCase();
  switch (authType) {
    case 'login_bundle':
      return 'Subscription-based (login bundle)';
    case 'api_key':
      if (normalized === 'claude' || normalized === 'claude-code') {
        return 'API-based (Anthropic token)';
      }
      if (normalized === 'junie' || normalized === 'junie-cli') {
        return 'API-based (Junie token)';
      }
      return 'API-based (API key)';
    case 'tokens':
      if (normalized === 'junie' || normalized === 'junie-cli') {
        return 'API-based (Junie tokens)';
      }
      return 'Subscription-based (manual tokens)';
    default:
      return authType;
  }
};

const legacyAuthHint = (authType: string, provider?: string | null): string | null => {
  const normalized = (provider ?? '').toLowerCase();
  if (normalized === 'codex' || normalized === 'claude' || normalized === 'claude-code') {
    if (authType === 'api_key' || authType === 'login_bundle' || authType === 'tokens') {
      return 'Obsolete for new sessions. Prefer AI Fence managed credentials.';
    }
  }
  return null;
};

// Auth form data type
interface AuthFormData {
  name: string;
  provider: ProviderKey;
  auth_type: AuthType;
  api_base: string;
  api_key: string;
  api_key_changed: boolean;
  access_token: string;
  access_token_changed: boolean;
  refresh_token: string;
  refresh_token_changed: boolean;
  id_token: string;
  id_token_changed: boolean;
  account_id: string;
  is_global: boolean;
}

const defaultAuthFormData = (): AuthFormData => ({
  name: '',
  provider: 'codex',
  auth_type: 'login_bundle',
  api_base: '',
  api_key: '',
  api_key_changed: false,
  access_token: '',
  access_token_changed: false,
  refresh_token: '',
  refresh_token_changed: false,
  id_token: '',
  id_token_changed: false,
  account_id: '',
  is_global: false
});

// Model form data type
interface ModelFormData {
  name: string;
  provider: ProviderKey;
  model: string;
  local_provider: string;
  auth_id: string;
  usage_provider_id: string;
  is_default: boolean;
  is_global: boolean;
  default_haiku_model: string;
  default_sonnet_model: string;
  default_opus_model: string;
  api_timeout_ms: string;
  model_reasoning_effort: string;
  model_auto_compact_mode: string;
  model_auto_compact_token_limit: string;
  model_turn_supervision_tokens: string;
}

const defaultModelFormData = (): ModelFormData => ({
  name: '',
  provider: 'codex',
  model: '',
  local_provider: '',
  auth_id: '',
  usage_provider_id: '',
  is_default: false,
  is_global: false,
  default_haiku_model: '',
  default_sonnet_model: '',
  default_opus_model: '',
  api_timeout_ms: '3000000',
  model_reasoning_effort: '',
  model_auto_compact_mode: 'codex_default',
  model_auto_compact_token_limit: '180000',
  model_turn_supervision_tokens: ''
});

// Usage provider form data type
// Usage providers are always linked to an auth entry to prevent misconfiguration when tokens change
interface UsageProviderFormData {
  name: string;
  provider: UsageProviderKind;
  linked_auth_id: string;
  config_json: string;
  is_global: boolean;
}

const defaultUsageProviderFormData = (): UsageProviderFormData => ({
  name: '',
  provider: 'kimi_code',
  linked_auth_id: '',
  config_json: '',
  is_global: false
});

// Check if the error indicates an expired token (OpenAI/Codex)
const isTokenExpiredError = (provider: UsageProvider): boolean => {
  const error = provider.latest_usage?.error?.toLowerCase() ?? '';
  return (
    error.includes('token_expired') ||
    error.includes('authentication token is expired') ||
    (error.includes('http 401') && provider.provider === 'codex')
  );
};

const usageStatusForProvider = (provider: UsageProvider): 'ok' | 'error' | 'expired' | 'unknown' => {
  const status = provider.latest_usage?.status?.toLowerCase();
  if (status === 'ok') return 'ok';
  if (status === 'error') {
    if (isTokenExpiredError(provider)) return 'expired';
    return 'error';
  }
  return 'unknown';
};

const usageStatusLabel = (provider: UsageProvider): string => {
  const status = usageStatusForProvider(provider);
  if (status === 'ok') return 'ok';
  if (status === 'expired') return 'expired';
  if (status === 'error') return 'error';
  return 'unknown';
};

const usageCellTitle = (provider: UsageProvider): string | undefined => {
  const capturedAt = provider.latest_usage?.captured_at
    ? new Date(provider.latest_usage.captured_at).toLocaleString()
    : null;
  const error = provider.latest_usage?.error?.trim();
  const hint = isTokenExpiredError(provider)
    ? '\n\nTo refresh: Start a conversation with Codex (the CLI will auto-refresh the token).'
    : '';
  if (capturedAt && error) return `${capturedAt}\n${error}${hint}`;
  if (capturedAt) return capturedAt;
  if (error) return `${error}${hint}`;
  return undefined;
};

// Helper to format usage display for different providers
const formatUsageDisplay = (provider: UsageProvider): string => {
  const usage = provider.latest_usage;
  const status = usageStatusForProvider(provider);
  if (!usage || status !== 'ok' || !usage.payload) {
    if (status === 'expired') return 'token expired';
    if (status === 'error') return 'error';
    return 'unknown';
  }

  const payload = usage.payload;

  switch (provider.provider) {
    case 'kimi_code':
    case 'moonshot': {
      const balance = payload.available_balance as number | undefined;
      if (typeof balance === 'number') {
        return `$${balance.toFixed(2)}`;
      }
      const remaining = payload.remaining as number | undefined;
      const total = payload.limit as number | undefined;
      if (typeof remaining === 'number' && typeof total === 'number' && total > 0) {
        return `${remaining}/${total} left`;
      }
      const weeklyLimit = (payload.weekly_limit ?? null) as Record<string, unknown> | null;
      const weeklyRemaining = Number(weeklyLimit?.remaining);
      const weeklyTotal = Number(weeklyLimit?.limit);
      if (
        Number.isFinite(weeklyRemaining) &&
        Number.isFinite(weeklyTotal) &&
        weeklyTotal > 0
      ) {
        return `${weeklyRemaining}/${weeklyTotal} left`;
      }
      const rateLimit =
        ((payload.primary_rate_limit ??
          ((payload.rate_limits as unknown[] | undefined)?.[0] ?? null)) as
          | Record<string, unknown>
          | null);
      const rateRemainingPercent = Number(rateLimit?.remaining_percent);
      if (Number.isFinite(rateRemainingPercent)) {
        return `week ${Math.max(0, rateRemainingPercent).toFixed(0)}% left`;
      }
      return 'unknown';
    }
    case 'openrouter': {
      const total = payload.total_credits as number | undefined;
      const used = payload.total_usage as number | undefined;
      if (typeof total === 'number' && typeof used === 'number') {
        const remaining = total - used;
        return `$${remaining.toFixed(2)} / $${total.toFixed(2)}`;
      }
      return 'unknown';
    }
    case 'minimax': {
      const total = payload.total_count as number | undefined;
      const used = payload.used_count as number | undefined;
      const remaining = payload.remaining_count as number | undefined;
      if (typeof total === 'number' && typeof remaining === 'number') {
        return `${remaining}/${total} left`;
      }
      if (typeof total === 'number' && typeof used === 'number') {
        const remainingFromUsed = Math.max(0, total - used);
        return `${remainingFromUsed}/${total} left`;
      }
      return 'unknown';
    }
    case 'zai': {
      const timeLimit = (payload.time_limit ?? null) as Record<string, unknown> | null;
      const tokensLimit = (payload.tokens_limit ?? null) as Record<string, unknown> | null;
      const level = payload.level as string | undefined;

      const parts: string[] = [];
      const timeRemaining = Number(timeLimit?.remaining);
      if (Number.isFinite(timeRemaining)) {
        parts.push(`time ${timeRemaining} left`);
      } else {
        const timeRemainingPercent = Number(timeLimit?.remaining_percent);
        if (Number.isFinite(timeRemainingPercent)) {
          parts.push(`time ${Math.max(0, timeRemainingPercent).toFixed(0)}% left`);
        }
      }
      const tokenRemainingPercent = Number(tokensLimit?.remaining_percent);
      if (Number.isFinite(tokenRemainingPercent)) {
        parts.push(`tokens ${Math.max(0, tokenRemainingPercent).toFixed(0)}% left`);
      }

      if (parts.length > 0) {
        return parts.join(' · ');
      }
      if (typeof level === 'string' && level.trim().length > 0) {
        return level.trim();
      }
      return 'unknown';
    }
    case 'codex': {
      // Use existing codex display logic
      const rateLimit = (payload.rate_limit ?? null) as Record<string, unknown> | null;
      const primary = (rateLimit?.primary_window ?? null) as Record<string, unknown> | null;
      const secondary = (rateLimit?.secondary_window ?? null) as Record<string, unknown> | null;

      const parts: string[] = [];
      const primaryUsed = Number(primary?.used_percent);
      if (Number.isFinite(primaryUsed)) {
        parts.push(`5h ${Math.max(0, 100 - primaryUsed)}% left`);
      }
      const secondaryUsed = Number(secondary?.used_percent);
      if (Number.isFinite(secondaryUsed)) {
        parts.push(`week ${Math.max(0, 100 - secondaryUsed)}% left`);
      }
      return parts.length > 0 ? parts.join(' · ') : 'unknown';
    }
    case 'claude': {
      const totalCost =
        (payload.total_cost_usd as number | undefined) ??
        (payload.total_cost as number | undefined);
      if (typeof totalCost === 'number') {
        return `$${totalCost.toFixed(2)}`;
      }
      return 'ok';
    }
    default:
      return 'unknown';
  }
};

const formatAutoCompact = (model: ModelConfig): string | null => {
  const mode = model.model_auto_compact_mode ?? 'codex_default';
  if (mode === 'custom') {
    return `compact: ${model.model_auto_compact_token_limit ?? 'custom'}`;
  }
  if (mode === 'disabled') {
    return 'compact: disabled';
  }
  return null;
};

const resolveSupervisionSettingLabel = (
  model: ModelConfig,
  supervisionModelTurnTokens: string
): string => {
  if (model.model_turn_supervision_tokens != null) {
    return `supervision: ${model.model_turn_supervision_tokens}`;
  }
  const trimmedGlobal = supervisionModelTurnTokens.trim();
  return `supervision: ${trimmedGlobal.length > 0 ? trimmedGlobal : 'unlimited'} (global)`;
};

const parsePositiveI32TokenLimit = (value: string, fieldLabel: string): number | null => {
  const raw = value.trim();
  if (raw.length === 0) {
    return null;
  }
  if (!/^\d+$/.test(raw)) {
    throw new Error(`${fieldLabel} must be a positive whole number`);
  }
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 2147483647) {
    throw new Error(`${fieldLabel} must be between 1 and 2147483647`);
  }
  return parsed;
};

export default function ModelSettingsPage({
  apiClient,
  setError,
  isAdmin
}: {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
  isAdmin: boolean;
}) {
  const [auths, setAuths] = useState<ModelAuth[]>([]);
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [aiFenceModels, setAiFenceModels] = useState<AiFenceModelCatalogItem[]>([]);
  const [aiFenceQuery, setAiFenceQuery] = useState('');
  const [aiFenceBusy, setAiFenceBusy] = useState(true);
  const [aiFenceFailed, setAiFenceFailed] = useState(false);
  const [supervisionModelTurnTokens, setSupervisionModelTurnTokens] = useState('');
  const [supervisionSettingsBusy, setSupervisionSettingsBusy] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [tokenBusy, setTokenBusy] = useState(false);
  const [modelError, setModelError] = useState<string | null>(null);
  const [gitlabConfigured, setGitlabConfigured] = useState<boolean | null>(null);
  const [gitlabBusy, setGitlabBusy] = useState(false);
  const [commitNameOverride, setCommitNameOverride] = useState('');
  const [commitNameDefault, setCommitNameDefault] = useState<string | null>(null);
  const [commitEmail, setCommitEmail] = useState<string | null>(null);
  const [commitNameBusy, setCommitNameBusy] = useState(false);
  const [commitNameStatus, setCommitNameStatus] = useState<string | null>(null);

  // Auth form state
  const [authFormData, setAuthFormData] = useState<AuthFormData>(defaultAuthFormData());
  const [editingAuthId, setEditingAuthId] = useState<string | null>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [uploadCommand, setUploadCommand] = useState<string | null>(null);
  const [uploadCommandCopied, setUploadCommandCopied] = useState(false);
  const [subscriptionVariant, setSubscriptionVariant] = useState<SubscriptionVariant>('automatic');
  const authFormRef = useRef<HTMLFormElement | null>(null);
  const uploadCommandCopyTimerRef = useRef<number | null>(null);

  // Model form state
  const [modelFormData, setModelFormData] = useState<ModelFormData>(defaultModelFormData());
  const [editingModelId, setEditingModelId] = useState<string | null>(null);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [modelDialogTab, setModelDialogTab] = useState<'basic' | 'advanced'>('basic');

  // Usage provider state
  const [usageProviders, setUsageProviders] = useState<UsageProvider[]>([]);
  const [usageProviderFormData, setUsageProviderFormData] = useState<UsageProviderFormData>(defaultUsageProviderFormData());
  const [editingUsageProviderId, setEditingUsageProviderId] = useState<string | null>(null);
  const [usageProviderDialogOpen, setUsageProviderDialogOpen] = useState(false);
  const [usageProviderBusy, setUsageProviderBusy] = useState(false);

  useEffect(() => {
    return () => {
      if (uploadCommandCopyTimerRef.current !== null) {
        window.clearTimeout(uploadCommandCopyTimerRef.current);
      }
    };
  }, []);

  const authTypesForProvider = (provider: ProviderKey): AuthType[] => {
    if (provider === 'claude') {
      return ['login_bundle', 'api_key'];
    }
    if (provider === 'junie') {
      return ['api_key', 'tokens'];
    }
    return ['login_bundle', 'api_key', 'tokens'];
  };

  const isCodexLikeProvider = (provider: ProviderKey) =>
    provider === 'codex' || provider === 'openai-compatible' || provider === 'oss';

  const authModeFor = (provider: ProviderKey, type: AuthType): AuthMode => {
    if (type === 'login_bundle') {
      return 'subscription';
    }
    if (type === 'tokens' && isCodexLikeProvider(provider)) {
      return 'subscription';
    }
    return 'api';
  };

  const manualSubscriptionKind = (provider: ProviderKey) => {
    if (provider === 'claude') {
      return 'overrides';
    }
    if (isCodexLikeProvider(provider)) {
      return 'tokens';
    }
    return null;
  };

  const supportsSubscription = (provider: ProviderKey) =>
    authTypesForProvider(provider).includes('login_bundle');

  const supportsTokens = (provider: ProviderKey) =>
    authTypesForProvider(provider).includes('tokens');

  const usageSummaryForAuth = (auth: ModelAuth): string => {
    const linkedProviders = usageProviders.filter((provider) => provider.linked_auth_id === auth.id);
    if (linkedProviders.length > 0) {
      const newestLinkedProvider = [...linkedProviders].sort((a, b) => {
        const aTs = a.latest_usage?.captured_at ? Date.parse(a.latest_usage.captured_at) : 0;
        const bTs = b.latest_usage?.captured_at ? Date.parse(b.latest_usage.captured_at) : 0;
        return bTs - aTs;
      })[0];
      return formatUsageDisplay(newestLinkedProvider);
    }

    const snapshot = auth.latest_usage;
    if (!snapshot || snapshot.status !== 'ok' || !snapshot.payload) {
      return 'unknown';
    }
    const payload = snapshot.payload;
    const rateLimit = (payload.rate_limit ?? null) as Record<string, unknown> | null;
    const primary = (rateLimit?.primary_window ?? null) as Record<string, unknown> | null;
    const secondary = (rateLimit?.secondary_window ?? null) as Record<string, unknown> | null;

    const parts: string[] = [];
    const primaryUsed = Number(primary?.used_percent);
    if (Number.isFinite(primaryUsed)) {
      parts.push(`5h ${Math.max(0, 100 - primaryUsed)}% left`);
    }
    const secondaryUsed = Number(secondary?.used_percent);
    if (Number.isFinite(secondaryUsed)) {
      parts.push(`week ${Math.max(0, 100 - secondaryUsed)}% left`);
    }

    if (parts.length === 0) {
      return 'unknown';
    }
    return parts.join(' · ');
  };

  const usageTitleForAuth = (auth: ModelAuth): string | undefined => {
    const linkedProviders = usageProviders.filter((provider) => provider.linked_auth_id === auth.id);
    if (linkedProviders.length > 0) {
      const newestLinkedProvider = [...linkedProviders].sort((a, b) => {
        const aTs = a.latest_usage?.captured_at ? Date.parse(a.latest_usage.captured_at) : 0;
        const bTs = b.latest_usage?.captured_at ? Date.parse(b.latest_usage.captured_at) : 0;
        return bTs - aTs;
      })[0];
      return usageCellTitle(newestLinkedProvider);
    }
    return auth.latest_usage?.captured_at
      ? new Date(auth.latest_usage.captured_at).toLocaleString()
      : undefined;
  };

  const loadCommitIdentity = useCallback(async () => {
    try {
      const data = await apiClient.get<MeResponse>('/api/me');
      const defaultName = data.user.name ?? data.user.email ?? null;
      setCommitNameDefault(defaultName);
      setCommitEmail(data.user.email ?? null);
      setCommitNameOverride(data.user.commit_name_override ?? '');
    } catch (err) {
      reportErrorLocal('Failed to load commit identity', err, setError, setModelError);
    }
  }, [apiClient, setError]);

  // Keep auth_type in sync when provider changes
  useEffect(() => {
    const allowed = authTypesForProvider(authFormData.provider);
    if (!allowed.includes(authFormData.auth_type)) {
      setAuthFormData((prev) => ({ ...prev, auth_type: allowed[0] }));
    }
  }, [authFormData.provider, authFormData.auth_type]);

  const loadAuths = useCallback(async () => {
    try {
      const data = await apiClient.get<ModelAuth[]>('/api/me/model-auths');
      setAuths(data);
    } catch (err) {
      reportErrorLocal('Failed to load model auths', err, setError, setModelError);
    }
  }, [apiClient, setError]);

  const loadModels = useCallback(async () => {
    try {
      const data = await apiClient.get<ModelConfig[]>('/api/me/models');
      setModels(data);
    } catch (err) {
      reportErrorLocal('Failed to load models', err, setError, setModelError);
    }
  }, [apiClient, setError]);

  const loadAiFenceModels = useCallback(async () => {
    setAiFenceBusy(true);
    setAiFenceFailed(false);
    try {
      const data = await apiClient.get<AiFenceModelCatalogItem[]>('/api/me/ai-fence-models');
      setAiFenceModels(data);
    } catch (err) {
      reportErrorLocal('Failed to load AI Fence models', err, setError, setModelError);
      setAiFenceFailed(true);
      setAiFenceModels([]);
    } finally {
      setAiFenceBusy(false);
    }
  }, [apiClient, setError]);

  const loadGitlabTokenStatus = useCallback(async () => {
    try {
      const data = await apiClient.get<{ configured: boolean }>('/api/me/gitlab-token');
      setGitlabConfigured(data.configured);
    } catch (err) {
      reportErrorLocal('Failed to load GitLab token status', err, setError, setModelError);
      setGitlabConfigured(null);
    }
  }, [apiClient, setError]);

  const loadSupervisionSettings = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await apiClient.get<SupervisionSettings>('/api/settings/supervision');
      setSupervisionModelTurnTokens(data.model_turn_supervision_tokens?.toString() ?? '');
    } catch (err) {
      reportErrorLocal('Failed to load model supervision settings', err, setError, setModelError);
    }
  }, [apiClient, isAdmin, setError]);

  function resetAuthForm() {
    if (uploadCommandCopyTimerRef.current !== null) {
      window.clearTimeout(uploadCommandCopyTimerRef.current);
      uploadCommandCopyTimerRef.current = null;
    }
    setUploadCommandCopied(false);
    setAuthFormData(defaultAuthFormData());
    setEditingAuthId(null);
    setUploadCommand(null);
    setSubscriptionVariant('automatic');
    setAuthDialogOpen(false);
  }

  function openCreateAuthDialog() {
    resetAuthForm();
    setAuthDialogOpen(true);
  }

  function startEditingAuth(auth: ModelAuth) {
    setEditingAuthId(auth.id);
    setAuthFormData({
      name: auth.name,
      provider: (auth.provider as ProviderKey) ?? 'codex',
      auth_type: auth.auth_type as AuthType,
      api_base: auth.api_base ?? '',
      api_key: '', // Don't populate passwords
      api_key_changed: false,
      access_token: '', // Don't populate tokens
      access_token_changed: false,
      refresh_token: '',
      refresh_token_changed: false,
      id_token: '',
      id_token_changed: false,
      account_id: '', // Will be loaded from auth
      is_global: auth.is_global
    });
    setAuthDialogOpen(true);
  }

  function resetModelForm() {
    setModelFormData(defaultModelFormData());
    setModelDialogTab('basic');
    setEditingModelId(null);
    setModelDialogOpen(false);
  }

  function openCreateModelDialog() {
    resetModelForm();
    setModelDialogOpen(true);
  }

  function startEditingModel(model: ModelConfig) {
    setEditingModelId(model.id);
    setModelFormData({
      name: model.name,
      provider: (model.provider as ProviderKey) ?? 'codex',
      model: model.model ?? '',
      local_provider: model.local_provider ?? '',
      auth_id: model.auth_id ?? '',
      usage_provider_id: model.usage_provider_id ?? '',
      is_default: model.is_default,
      is_global: model.is_global,
      default_haiku_model: model.default_haiku_model ?? '',
      default_sonnet_model: model.default_sonnet_model ?? '',
      default_opus_model: model.default_opus_model ?? '',
      api_timeout_ms: model.api_timeout_ms ?? '3000000',
      model_reasoning_effort: model.model_reasoning_effort ?? '',
      model_auto_compact_mode: model.model_auto_compact_mode ?? 'codex_default',
      model_auto_compact_token_limit: String(model.model_auto_compact_token_limit ?? 180000),
      model_turn_supervision_tokens: String(model.model_turn_supervision_tokens ?? '')
    });
    setModelDialogOpen(true);
  }

  // Usage provider functions
  const loadUsageProviders = useCallback(async () => {
    try {
      const data = await apiClient.get<UsageProvider[]>('/api/me/usage-providers');
      setUsageProviders(data);
    } catch (err) {
      reportErrorLocal('Failed to load usage providers', err, setError, setModelError);
    }
  }, [apiClient, setError]);

  useEffect(() => {
    loadAuths();
    loadModels();
    loadAiFenceModels();
    loadUsageProviders();
    loadGitlabTokenStatus();
    loadSupervisionSettings();
    loadCommitIdentity();
  }, [
    loadAiFenceModels,
    loadAuths,
    loadCommitIdentity,
    loadGitlabTokenStatus,
    loadModels,
    loadSupervisionSettings,
    loadUsageProviders
  ]);

  function resetUsageProviderForm() {
    setUsageProviderFormData(defaultUsageProviderFormData());
    setEditingUsageProviderId(null);
    setUsageProviderDialogOpen(false);
  }

  function openCreateUsageProviderDialog() {
    resetUsageProviderForm();
    setUsageProviderDialogOpen(true);
  }

  function startEditingUsageProvider(provider: UsageProvider) {
    setEditingUsageProviderId(provider.id);
    setUsageProviderFormData({
      name: provider.name,
      provider: provider.provider,
      linked_auth_id: provider.linked_auth_id ?? '',
      config_json: provider.config_json ? JSON.stringify(provider.config_json, null, 2) : '',
      is_global: provider.is_global
    });
    setUsageProviderDialogOpen(true);
  }

  async function saveUsageProvider(e: React.FormEvent) {
    e.preventDefault();
    if (usageProviderBusy) return;

    if (!usageProviderFormData.name.trim()) {
      setError('Usage provider name required');
      return;
    }

    if (!usageProviderFormData.linked_auth_id) {
      setError('Linked auth is required - usage providers must be linked to an auth entry');
      return;
    }

    const payload: Record<string, unknown> = {
      name: usageProviderFormData.name.trim(),
      provider: usageProviderFormData.provider,
      linked_auth_id: usageProviderFormData.linked_auth_id,
      is_global: isAdmin ? usageProviderFormData.is_global : false
    };

    // Parse config_json if provided
    if (usageProviderFormData.config_json.trim()) {
      try {
        payload.config_json = JSON.parse(usageProviderFormData.config_json);
      } catch {
        setError('Invalid JSON in config field');
        return;
      }
    }

    setUsageProviderBusy(true);
    try {
      if (editingUsageProviderId) {
        await apiClient.patch(`/api/me/usage-providers/${editingUsageProviderId}`, payload);
      } else {
        await apiClient.post('/api/me/usage-providers', payload);
      }
      resetUsageProviderForm();
      await loadUsageProviders();
    } catch (err) {
      reportErrorLocal(
        editingUsageProviderId ? 'Failed to update usage provider' : 'Failed to create usage provider',
        err,
        setError,
        setModelError
      );
    } finally {
      setUsageProviderBusy(false);
    }
  }

  async function deleteUsageProvider(provider: UsageProvider) {
    if (!window.confirm(`Delete usage provider "${provider.name}"?`)) return;
    try {
      await apiClient.del(`/api/me/usage-providers/${provider.id}`);
      await loadUsageProviders();
    } catch (err) {
      reportErrorLocal('Failed to delete usage provider', err, setError, setModelError);
    }
  }

  async function toggleUsageProviderGlobal(provider: UsageProvider) {
    if (!isAdmin) return;
    const newScope = provider.is_global ? 'user' : 'global';
    if (!window.confirm(`Make usage provider "${provider.name}" ${newScope}?`)) return;
    try {
      await apiClient.patch(`/api/me/usage-providers/${provider.id}`, { is_global: !provider.is_global });
      await loadUsageProviders();
    } catch (err) {
      reportErrorLocal('Failed to update usage provider scope', err, setError, setModelError);
    }
  }

  async function testUsageProvider(provider: UsageProvider) {
    try {
      // Call the refresh endpoint to fetch real usage from the provider's API
      await apiClient.post(`/api/me/usage-providers/${provider.id}/refresh`);
      await loadUsageProviders();
    } catch (err) {
      reportErrorLocal('Failed to refresh usage provider', err, setError, setModelError);
    }
  }

  async function saveAuth(e: React.FormEvent) {
    e.preventDefault();
    if (authBusy) return;
    const isEditing = editingAuthId !== null;

    if (!authFormData.name.trim()) {
      setError('Auth name required');
      return;
    }

    const payload: Record<string, unknown> = {
      name: authFormData.name.trim(),
      provider: authFormData.provider,
      auth_type: authFormData.auth_type,
      api_base: authFormData.api_base.trim() || null,
      account_id: authFormData.account_id.trim() || null,
      is_global: isAdmin ? authFormData.is_global : false
    };

    // Only include password fields if they were changed
    if (authFormData.api_key_changed && authFormData.api_key.trim()) {
      payload.api_key = authFormData.api_key.trim();
      payload.api_key_changed = true;
    }
    if (authFormData.access_token_changed && authFormData.access_token.trim()) {
      payload.access_token = authFormData.access_token.trim();
      payload.access_token_changed = true;
    }
    if (authFormData.refresh_token_changed && authFormData.refresh_token.trim()) {
      payload.refresh_token = authFormData.refresh_token.trim();
      payload.refresh_token_changed = true;
    }
    if (authFormData.id_token_changed && authFormData.id_token.trim()) {
      payload.id_token = authFormData.id_token.trim();
      payload.id_token_changed = true;
    }

    // Handle login bundle file upload for subscription-based auth
    if (authFormData.auth_type === 'login_bundle' && subscriptionVariant === 'automatic') {
      // Check if a file was selected for upload
      const fileInput = authFormRef.current?.querySelector('input[name="login_bundle"]') as HTMLInputElement | null;
      const files = fileInput?.files;
      if (files && files.length > 0) {
        // Upload the selected zip file
        await uploadLoginBundleFiles(files);
        return;
      }
      // Creating a new auth still requires a bundle in automatic mode.
      // Editing an existing auth may update metadata (for example rename) without re-upload.
      if (!isEditing) {
        setModelError('Please select a zip file to upload or switch to manual configuration');
        return;
      }
    }

    // For non-admins, always set is_global to false
    if (!isAdmin) {
      payload.is_global = false;
    }

    setAuthBusy(true);
    try {
      if (editingAuthId) {
        // Update existing auth - unified endpoint handles both user and global
        await apiClient.patch(`/api/me/model-auths/${editingAuthId}`, payload);
      } else {
        // Create new auth - unified endpoint handles both user and global
        await apiClient.post('/api/me/model-auths', payload);
      }
      resetAuthForm();
      await loadAuths();
    } catch (err) {
      reportErrorLocal(
        editingAuthId ? 'Failed to update auth' : 'Failed to create auth',
        err,
        setError,
        setModelError
      );
    } finally {
      setAuthBusy(false);
    }
  }

  const escapePowershell = (value: string) =>
    value.replace(/`/g, '``').replace(/\$/g, '`$').replace(/"/g, '`"');

  async function buildUploadCommand(scope: 'user' | 'global', platform: ScriptPlatform) {
    const apiBase = window.location.origin;
    const endpoint =
      scope === 'global' ? '/api/admin/model-auths/upload-token' : '/api/me/model-auths/upload-token';
    setTokenBusy(true);
    try {
      const tokenResponse = await apiClient.post<{ token: string }>(endpoint, {});
      const token = tokenResponse.token;
      const trimmedName = authFormData.name.trim();
      const suggestedName =
        trimmedName ||
        (scope === 'global' ? `Global ${authFormData.provider} bundle` : `${authFormData.provider} login bundle`);
      const envParts: Array<{ key: string; value: string }> = [
        { key: 'PROVIDER', value: authFormData.provider },
        { key: 'AUTH_NAME', value: suggestedName },
        { key: 'AUTH_TOKEN', value: token },
        { key: 'API_BASE', value: apiBase }
      ];
      if (editingAuthId) {
        envParts.push({ key: 'AUTH_ID', value: editingAuthId });
      }
      if (authFormData.provider === 'claude') {
        const authToken = authFormData.api_key.trim();
        const baseUrl = authFormData.api_base.trim();
        if (authToken) {
          envParts.push({ key: 'ANTHROPIC_AUTH_TOKEN', value: authToken });
        }
        if (baseUrl) {
          envParts.push({ key: 'ANTHROPIC_BASE_URL', value: baseUrl });
        }
      }
      const cmd =
        platform === 'windows'
          ? `${envParts
              .map(({ key, value }) => `$env:${key}="${escapePowershell(value)}"`)
              .join('; ')}; iwr -useb "${apiBase}/api/model-auths/bootstrap.ps1" | iex`
          : `${envParts
              .map(({ key, value }) => `${key}="${value.replace(/"/g, '\\"')}"`)
              .join(' ')} curl -fsSL "${apiBase}/api/model-auths/bootstrap.sh" | ${envParts
              .map(({ key, value }) => `${key}="${value.replace(/"/g, '\\"')}"`)
              .join(' ')} bash`;
      setUploadCommand(cmd);
      setUploadCommandCopied(false);
      await copyCommand(cmd);
    } catch (err) {
      reportErrorLocal('Failed to create upload command', err, setError, setModelError);
    } finally {
      setTokenBusy(false);
    }
  }

  async function uploadLoginBundleFiles(files: FileList) {
    // Find the first zip file
    const zipFile = Array.from(files).find(f => f.name.endsWith('.zip'));
    if (!zipFile) {
      setModelError('Please select a valid zip file');
      return;
    }

    setAuthBusy(true);
    setModelError(null);

    try {
      // Get upload token
      const endpoint = authFormData.is_global
        ? '/api/admin/model-auths/upload-token'
        : '/api/me/model-auths/upload-token';
      const tokenResponse = await apiClient.post<{ token: string }>(endpoint, {});
      const token = tokenResponse.token;

      // Read file as base64
      const arrayBuffer = await zipFile.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      // Upload the bundle
      const trimmedName = authFormData.name.trim();
      const suggestedName = authFormData.is_global
        ? trimmedName || `Global ${authFormData.provider} bundle`
        : trimmedName || `${authFormData.provider} login bundle`;

      await apiClient.post('/api/model-auths/upload', {
        token,
        name: suggestedName,
        provider: authFormData.provider,
        auth_id: editingAuthId ?? undefined,
        login_bundle_b64: base64
      });

      // Success - close dialog and refresh
      resetAuthForm();
      await loadAuths();
    } catch (err) {
      reportErrorLocal('Failed to upload login bundle', err, setError, setModelError);
    } finally {
      setAuthBusy(false);
    }
  }

  async function saveSupervisionSettings(e: React.FormEvent) {
    e.preventDefault();
    if (!isAdmin) return;
    if (supervisionSettingsBusy) return;
    let parsedLimit: number | null;
    try {
      parsedLimit = parsePositiveI32TokenLimit(
        supervisionModelTurnTokens,
        'Global turn supervision token limit'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid global turn supervision token limit');
      return;
    }

    setSupervisionSettingsBusy(true);
    try {
      const saved = await apiClient.post<SupervisionSettings>('/api/settings/supervision', {
        model_turn_supervision_tokens: parsedLimit
      });
      setSupervisionModelTurnTokens(saved.model_turn_supervision_tokens?.toString() ?? '');
    } catch (err) {
      reportErrorLocal(
        'Failed to save global turn supervision settings',
        err,
        setError,
        setModelError
      );
    } finally {
      setSupervisionSettingsBusy(false);
    }
  }

  async function saveModel(e: React.FormEvent) {
    e.preventDefault();
    if (!modelFormData.name.trim()) {
      setError('Model name required');
      return;
    }
    const autoCompactLimitRaw = modelFormData.model_auto_compact_token_limit.trim();
    const autoCompactLimit =
      autoCompactLimitRaw.length > 0 ? Number.parseInt(autoCompactLimitRaw, 10) : null;
    if (
      modelFormData.model_auto_compact_mode === 'custom' &&
      (!/^\d+$/.test(autoCompactLimitRaw) ||
        !Number.isFinite(autoCompactLimit) ||
        autoCompactLimit === null ||
        autoCompactLimit <= 0)
    ) {
      setError('Custom auto-compact token limit must be a positive number');
      return;
    }
    let modelTurnSupervisionLimit: number | null;
    try {
      modelTurnSupervisionLimit = parsePositiveI32TokenLimit(
        modelFormData.model_turn_supervision_tokens,
        'Per-model turn supervision token limit'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid per-model turn supervision token limit');
      return;
    }

    const payload: Record<string, unknown> = {
      name: modelFormData.name.trim(),
      provider: modelFormData.provider,
      model: modelFormData.model.trim() || null,
      local_provider: modelFormData.local_provider.trim() || null,
      auth_id: modelFormData.auth_id || null,
      usage_provider_id: modelFormData.usage_provider_id || null,
      is_default: modelFormData.is_default,
      is_global: isAdmin ? modelFormData.is_global : false,
      default_haiku_model: modelFormData.default_haiku_model.trim() || null,
      default_sonnet_model: modelFormData.default_sonnet_model.trim() || null,
      default_opus_model: modelFormData.default_opus_model.trim() || null,
      api_timeout_ms: modelFormData.api_timeout_ms.trim() || null,
      model_reasoning_effort: modelFormData.model_reasoning_effort || null,
      model_auto_compact_mode: modelFormData.model_auto_compact_mode || 'codex_default',
      model_auto_compact_token_limit:
        modelFormData.model_auto_compact_mode === 'custom' ? autoCompactLimit : null,
      model_turn_supervision_tokens: modelTurnSupervisionLimit
    };

    // Clear model-specific fields for non-Claude providers
    if (modelFormData.provider !== 'claude') {
      payload.default_haiku_model = null;
      payload.default_sonnet_model = null;
      payload.default_opus_model = null;
    }
    if (!isCodexLikeProvider(modelFormData.provider)) {
      payload.model_reasoning_effort = null;
      payload.model_auto_compact_mode = null;
      payload.model_auto_compact_token_limit = null;
    }

    try {
      if (editingModelId) {
        // Update existing model - unified endpoint handles both user and global
        await apiClient.patch(`/api/me/models/${editingModelId}`, payload);
      } else {
        // Create new model - unified endpoint handles both user and global
        await apiClient.post('/api/me/models', payload);
      }
      resetModelForm();
      await loadModels();
      await loadAiFenceModels();
    } catch (err) {
      reportErrorLocal(
        editingModelId ? 'Failed to update model' : 'Failed to create model',
        err,
        setError,
        setModelError
      );
    }
  }

  async function deleteAuth(auth: ModelAuth) {
    if (!window.confirm(`Delete auth "${auth.name}"?`)) return;
    try {
      // Use unified endpoint
      await apiClient.del(`/api/me/model-auths/${auth.id}`);
      await loadAuths();
    } catch (err) {
      reportErrorLocal('Failed to delete auth', err, setError, setModelError);
    }
  }

  async function toggleAuthGlobal(auth: ModelAuth) {
    if (!isAdmin) return;
    const newScope = auth.is_global ? 'user' : 'global';
    if (!window.confirm(`Make auth "${auth.name}" ${newScope}?`)) return;
    try {
      // Use unified endpoint
      await apiClient.patch(`/api/me/model-auths/${auth.id}`, { is_global: !auth.is_global });
      await loadAuths();
    } catch (err) {
      reportErrorLocal('Failed to update auth scope', err, setError, setModelError);
    }
  }

  async function copyCommand(value: string | null) {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setUploadCommandCopied(true);
      if (uploadCommandCopyTimerRef.current !== null) {
        window.clearTimeout(uploadCommandCopyTimerRef.current);
      }
      uploadCommandCopyTimerRef.current = window.setTimeout(() => {
        setUploadCommandCopied(false);
        uploadCommandCopyTimerRef.current = null;
      }, 2000);
    } catch {
      setError('Failed to copy command');
      setUploadCommandCopied(false);
    }
  }

  const maskToken = (value: string | null) => {
    if (!value) return '';
    return value
      .replace(/AUTH_TOKEN="[^"]+"/g, 'AUTH_TOKEN="••••"')
      .replace(/ANTHROPIC_AUTH_TOKEN="[^"]+"/g, 'ANTHROPIC_AUTH_TOKEN="••••"');
  };

  async function deleteModel(model: ModelConfig) {
    if (!window.confirm(`Delete model "${model.name}"?`)) return;
    try {
      // Use unified endpoint
      await apiClient.del(`/api/me/models/${model.id}`);
      await loadModels();
      await loadAiFenceModels();
    } catch (err) {
      reportErrorLocal('Failed to delete model', err, setError, setModelError);
    }
  }

  async function setDefault(model: ModelConfig) {
    try {
      // Use unified endpoint
      await apiClient.patch(`/api/me/models/${model.id}`, { is_default: true });
      await loadModels();
    } catch (err) {
      reportErrorLocal('Failed to set default model', err, setError, setModelError);
    }
  }

  async function toggleModelGlobal(model: ModelConfig) {
    if (!isAdmin) return;
    const newScope = model.is_global ? 'user' : 'global';
    if (!window.confirm(`Make model "${model.name}" ${newScope}?`)) return;
    try {
      // Use unified endpoint
      await apiClient.patch(`/api/me/models/${model.id}`, { is_global: !model.is_global });
      await loadModels();
    } catch (err) {
      reportErrorLocal('Failed to update model scope', err, setError, setModelError);
    }
  }

  async function createAiFenceModelProfile(item: AiFenceModelCatalogItem) {
    try {
      const created = await apiClient.post<ModelConfig>('/api/me/models', {
        name:
          item.enabled_profile_count > 0
            ? `${item.name} (${item.enabled_profile_count + 1})`
            : item.name,
        provider: item.provider,
        model: item.model,
        local_provider: null,
        auth_id: null,
        usage_provider_id: null,
        is_default: false,
        is_global: false,
        default_haiku_model: null,
        default_sonnet_model: null,
        default_opus_model: null,
        api_timeout_ms: item.provider === 'claude' ? '3000000' : null,
        model_reasoning_effort: null,
        model_auto_compact_mode: item.provider === 'codex' ? 'codex_default' : null,
        model_auto_compact_token_limit: null,
        model_turn_supervision_tokens: null
      });
      await loadModels();
      await loadAiFenceModels();
      startEditingModel(created);
      setModelDialogTab(item.provider === 'codex' ? 'advanced' : 'basic');
    } catch (err) {
      reportErrorLocal('Failed to create AI Fence profile', err, setError, setModelError);
    }
  }

  const authMode = authModeFor(authFormData.provider, authFormData.auth_type);
  const authHasSubscription = supportsSubscription(authFormData.provider);
  const authSupportsTokens = supportsTokens(authFormData.provider);
  const authManualKind = manualSubscriptionKind(authFormData.provider);
  const authManualSubscriptionAvailable = authHasSubscription && authManualKind !== null;

  const authApiPanelClass = `auth-panel${authMode === 'api' ? ' auth-panel--active' : ''}`;
  const authBundlePanelClass = `auth-panel${authMode === 'subscription' ? ' auth-panel--active' : ''}`;

  // Derive model-specific computed values from form data
  const modelProvider = modelFormData.provider;
  const isGlobalModel = modelFormData.is_global;
  const filteredAiFenceModels = useMemo(() => {
    const query = aiFenceQuery.trim().toLowerCase();
    const filtered = query
      ? aiFenceModels.filter((item) =>
          [item.name, item.provider, item.model].some((value) =>
            value.toLowerCase().includes(query)
          )
        )
      : aiFenceModels;
    return filtered.slice(0, 60);
  }, [aiFenceModels, aiFenceQuery]);
  const aiFenceMatchCount = useMemo(() => {
    const query = aiFenceQuery.trim().toLowerCase();
    if (!query) return aiFenceModels.length;
    return aiFenceModels.filter((item) =>
      [item.name, item.provider, item.model].some((value) => value.toLowerCase().includes(query))
    ).length;
  }, [aiFenceModels, aiFenceQuery]);

  // Render the auth form fields based on provider and auth type
  const renderAuthFields = () => {
    const isEditing = editingAuthId !== null;

    if (authMode === 'api') {
      return (
        <div className={authApiPanelClass}>
          <h3>API-based</h3>
          {authFormData.provider === 'claude' ? (
            <>
              <input
                name="api_base"
                placeholder="Anthropic base URL (optional)"
                value={authFormData.api_base}
                onChange={(e) => setAuthFormData((prev) => ({ ...prev, api_base: e.target.value }))}
              />
              <input
                name="api_key"
                type="password"
                placeholder={isEditing ? 'Anthropic auth token (leave blank to keep current)' : 'Anthropic auth token'}
                value={authFormData.api_key}
                onChange={(e) =>
                  setAuthFormData((prev) => ({ ...prev, api_key: e.target.value, api_key_changed: true }))
                }
              />
            </>
          ) : authFormData.provider === 'junie' && authSupportsTokens ? (
            <>
              <label className="checkbox-row">
                <span>Credential type</span>
                <select
                  value={authFormData.auth_type}
                  onChange={(e) =>
                    setAuthFormData((prev) => ({ ...prev, auth_type: e.target.value as AuthType }))
                  }
                >
                  <option value="api_key">API token</option>
                  <option value="tokens">OAuth tokens</option>
                </select>
              </label>
              {authFormData.auth_type === 'api_key' && (
                <>
                  <input
                    name="api_base"
                    placeholder="API base (optional)"
                    value={authFormData.api_base}
                    onChange={(e) =>
                      setAuthFormData((prev) => ({ ...prev, api_base: e.target.value }))
                    }
                  />
                  <input
                    name="api_key"
                    type="password"
                    placeholder={isEditing ? 'API key (leave blank to keep current)' : 'API key'}
                    value={authFormData.api_key}
                    onChange={(e) =>
                      setAuthFormData((prev) => ({
                        ...prev,
                        api_key: e.target.value,
                        api_key_changed: true
                      }))
                    }
                  />
                </>
              )}
              {authFormData.auth_type === 'tokens' && (
                <>
                  <input
                    name="access_token"
                    placeholder="Access token"
                    value={authFormData.access_token}
                    onChange={(e) =>
                      setAuthFormData((prev) => ({
                        ...prev,
                        access_token: e.target.value,
                        access_token_changed: true
                      }))
                    }
                  />
                  <input
                    name="refresh_token"
                    placeholder="Refresh token (optional)"
                    value={authFormData.refresh_token}
                    onChange={(e) =>
                      setAuthFormData((prev) => ({
                        ...prev,
                        refresh_token: e.target.value,
                        refresh_token_changed: true
                      }))
                    }
                  />
                  <input
                    name="id_token"
                    placeholder="ID token (optional)"
                    value={authFormData.id_token}
                    onChange={(e) =>
                      setAuthFormData((prev) => ({
                        ...prev,
                        id_token: e.target.value,
                        id_token_changed: true
                      }))
                    }
                  />
                  <input
                    name="account_id"
                    placeholder="Account ID (optional)"
                    value={authFormData.account_id}
                    onChange={(e) =>
                      setAuthFormData((prev) => ({ ...prev, account_id: e.target.value }))
                    }
                  />
                </>
              )}
            </>
          ) : (
            <>
              <input
                name="api_base"
                placeholder="API base (optional)"
                value={authFormData.api_base}
                onChange={(e) => setAuthFormData((prev) => ({ ...prev, api_base: e.target.value }))}
              />
              <input
                name="api_key"
                type="password"
                placeholder={isEditing ? 'API key (leave blank to keep current)' : 'API key'}
                value={authFormData.api_key}
                onChange={(e) =>
                  setAuthFormData((prev) => ({ ...prev, api_key: e.target.value, api_key_changed: true }))
                }
              />
              <input
                name="account_id"
                placeholder="Account ID (optional)"
                value={authFormData.account_id}
                onChange={(e) =>
                  setAuthFormData((prev) => ({ ...prev, account_id: e.target.value }))
                }
              />
            </>
          )}
        </div>
      );
    }

    // Subscription mode
    return (
      <>
        {authManualSubscriptionAvailable && (
          <div className="auth-mode-row">
            <label className="checkbox-row">
              <input
                type="radio"
                name="subscription_method"
                checked={subscriptionVariant === 'automatic'}
                onChange={() => setSubscriptionVariant('automatic')}
              />
              <span>Automatic (login bundle)</span>
            </label>
            <label className="checkbox-row">
              <input
                type="radio"
                name="subscription_method"
                checked={subscriptionVariant === 'manual'}
                onChange={() => setSubscriptionVariant('manual')}
              />
              <span>{authManualKind === 'overrides' ? 'Manual (overrides)' : 'Manual (tokens)'}</span>
            </label>
          </div>
        )}
        {subscriptionVariant === 'automatic' ? (
          <div className={authBundlePanelClass}>
            <h3>Automatic</h3>
            <p className="status">
              Select a login bundle zip file to upload, or use the command below to generate one
              automatically.
            </p>
            <input
              name="login_bundle"
              type="file"
              accept=".zip"
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  // Clear any previous error when a new file is selected
                  setModelError(null);
                }
              }}
            />
            {authFormData.auth_type === 'login_bundle' && (
              <div className="stack">
                <p className="status">
                  Generate a one-time upload command that runs the provider login locally and uploads
                  the bundle automatically.
                </p>
                <div className="auth-platform-row">
                  <button
                    type="button"
                    className="mini-button"
                    onClick={() => buildUploadCommand(authFormData.is_global ? 'global' : 'user', 'unix')}
                    disabled={tokenBusy}
                  >
                    <span className="os-icon">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" fill="none" />
                        <circle cx="9" cy="10" r="1" fill="currentColor" />
                        <circle cx="15" cy="10" r="1" fill="currentColor" />
                        <path d="M8 15c1.6 1.6 6.4 1.6 8 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    </span>
                    {tokenBusy ? 'Generating…' : 'Linux/macOS'}
                  </button>
                  <button
                    type="button"
                    className="mini-button"
                    onClick={() => buildUploadCommand(authFormData.is_global ? 'global' : 'user', 'windows')}
                    disabled={tokenBusy}
                  >
                    <span className="os-icon">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <rect x="4" y="4" width="7" height="7" fill="currentColor" />
                        <rect x="13" y="4" width="7" height="7" fill="currentColor" />
                        <rect x="4" y="13" width="7" height="7" fill="currentColor" />
                        <rect x="13" y="13" width="7" height="7" fill="currentColor" />
                      </svg>
                    </span>
                    {tokenBusy ? 'Generating…' : 'Windows'}
                  </button>
                </div>
                {uploadCommand && (
                  <div className="stack">
                    <div className="row">
                      <span className="mono">Command</span>
                      <button type="button" className="ghost" onClick={() => copyCommand(uploadCommand)}>
                        {uploadCommandCopied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <textarea className="log-box" rows={3} readOnly value={maskToken(uploadCommand)} />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={authApiPanelClass}>
            <h3>Manual</h3>
            {authManualKind === 'overrides' ? (
              <>
                <p className="status">Optional overrides written into the bundle&apos;s settings.json.</p>
                <input
                  name="api_base"
                  placeholder="Anthropic base URL (optional)"
                  value={authFormData.api_base}
                  onChange={(e) => setAuthFormData((prev) => ({ ...prev, api_base: e.target.value }))}
                />
                <input
                  name="api_key"
                  type="password"
                  placeholder="Anthropic auth token (optional)"
                  value={authFormData.api_key}
                  onChange={(e) =>
                    setAuthFormData((prev) => ({ ...prev, api_key: e.target.value, api_key_changed: true }))
                  }
                />
              </>
            ) : authManualKind === 'tokens' ? (
              <>
                <input
                  name="access_token"
                  placeholder="Access token"
                  value={authFormData.access_token}
                  onChange={(e) =>
                    setAuthFormData((prev) => ({
                      ...prev,
                      access_token: e.target.value,
                      access_token_changed: true
                    }))
                  }
                />
                <input
                  name="refresh_token"
                  placeholder="Refresh token (optional)"
                  value={authFormData.refresh_token}
                  onChange={(e) =>
                    setAuthFormData((prev) => ({
                      ...prev,
                      refresh_token: e.target.value,
                      refresh_token_changed: true
                    }))
                  }
                />
                <input
                  name="id_token"
                  placeholder="ID token (optional)"
                  value={authFormData.id_token}
                  onChange={(e) =>
                    setAuthFormData((prev) => ({ ...prev, id_token: e.target.value, id_token_changed: true }))
                  }
                />
                <input
                  name="account_id"
                  placeholder="Account ID (optional)"
                  value={authFormData.account_id}
                  onChange={(e) =>
                    setAuthFormData((prev) => ({ ...prev, account_id: e.target.value }))
                  }
                />
              </>
            ) : (
              <p className="status">Manual subscription tokens are not supported for this provider.</p>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <section className="grid model-settings-page">
      <div className="card">
        <h2>Commit identity</h2>
        {commitNameStatus && (
          <div className="status" role="status" style={{ color: 'var(--color-success-text)', marginBottom: 'var(--space-md)' }}>
            {commitNameStatus}
          </div>
        )}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (commitNameBusy) return;
            setCommitNameBusy(true);
            setCommitNameStatus(null);
            try {
              await apiClient.post('/api/me/commit-name', {
                name: commitNameOverride.trim() || null
              });
              setCommitNameStatus('Commit name updated');
              await loadCommitIdentity();
            } catch (err) {
              reportErrorLocal('Failed to update commit name', err, setError, setModelError);
            } finally {
              setCommitNameBusy(false);
            }
          }}
        >
          <label htmlFor="model-settings-commit-name">Commit author name</label>
          <input
            id="model-settings-commit-name"
            aria-describedby="model-settings-commit-hint"
            name="commit_name"
            placeholder={commitNameDefault ?? 'Commit author name'}
            value={commitNameOverride}
            onChange={(e) => setCommitNameOverride(e.target.value)}
          />
          <div className="row">
            <button type="submit" disabled={commitNameBusy}>
              {commitNameBusy ? 'Saving…' : 'Save name'}
            </button>
            {commitNameOverride && (
              <button
                type="button"
                className="secondary"
                disabled={commitNameBusy}
                onClick={() => setCommitNameOverride('')}
              >
                Clear override
              </button>
            )}
          </div>
        </form>
        <p className="hint" id="model-settings-commit-hint">
          Commits use your verified email <span className="mono">{commitEmail ?? 'unknown'}</span>. Leave
          the name blank to use {commitNameDefault ? `"${commitNameDefault}"` : 'your profile name'}.
        </p>
      </div>

      <div className="card">
        <h2>GitLab token</h2>
        {gitlabConfigured !== null && (
          <p className="status">Status: {gitlabConfigured ? 'configured' : 'not configured'}</p>
        )}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const formEl = e.currentTarget;
            if (gitlabBusy) return;
            const form = new FormData(formEl);
            const token = String(form.get('gitlab_token') ?? '').trim();
            if (!token) {
              setError('GitLab token required');
              return;
            }
            setGitlabBusy(true);
            try {
              await apiClient.post('/api/me/gitlab-token', { token });
              formEl.reset();
              await loadGitlabTokenStatus();
            } catch (err) {
              reportErrorLocal('Failed to save GitLab token', err, setError, setModelError);
            } finally {
              setGitlabBusy(false);
            }
          }}
        >
          <label htmlFor="model-settings-gitlab-token">GitLab API token</label>
          <input id="model-settings-gitlab-token" name="gitlab_token" placeholder="GitLab API token" type="password" required />
          <div className="row">
            <button type="submit" disabled={gitlabBusy}>
              {gitlabBusy ? 'Saving…' : 'Save token'}
            </button>
            {gitlabConfigured && (
              <button
                type="button"
                onClick={async () => {
                  if (gitlabBusy) return;
                  setGitlabBusy(true);
                  try {
                    await apiClient.del('/api/me/gitlab-token');
                    await loadGitlabTokenStatus();
                  } catch (err) {
                    reportErrorLocal('Failed to clear GitLab token', err, setError, setModelError);
                  } finally {
                    setGitlabBusy(false);
                  }
                }}
              >
                Clear token
              </button>
            )}
          </div>
        </form>
      </div>

      {isAdmin && (
        <div className="card">
          <h2>Turn supervision</h2>
          <p className="status" id="model-settings-supervision-hint">
            Set a global default token budget for runner turn supervision. Empty means unlimited.
          </p>
          <form onSubmit={saveSupervisionSettings}>
            <label htmlFor="model-settings-supervision">Global turn token limit</label>
            <input
              id="model-settings-supervision"
              aria-describedby="model-settings-supervision-hint"
              name="model_turn_supervision_tokens"
              inputMode="numeric"
              placeholder="Global turn tokens (empty = unlimited)"
              value={supervisionModelTurnTokens}
              onChange={(e) => setSupervisionModelTurnTokens(e.target.value)}
            />
            <div className="row">
              <button type="submit" disabled={supervisionSettingsBusy}>
                {supervisionSettingsBusy ? 'Saving…' : 'Save global limit'}
              </button>
              <button
                type="button"
                onClick={loadSupervisionSettings}
                disabled={supervisionSettingsBusy}
              >
                Refresh
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Auth Dialog */}
      <Dialog
        title={editingAuthId ? 'Edit Auth' : 'Create Auth'}
        open={authDialogOpen}
        onClose={resetAuthForm}
        size="medium"
        footer={
          <div className="row" style={{ justifyContent: 'flex-end', width: '100%' }}>
            <button type="button" className="secondary" onClick={resetAuthForm}>
              Cancel
            </button>
            <button type="submit" form="auth-form" disabled={authBusy}>
              {authBusy ? 'Saving…' : editingAuthId ? 'Update auth' : 'Create auth'}
            </button>
          </div>
        }
      >
        {modelError && <ErrorBanner message={modelError} onDismiss={() => setModelError(null)} />}
        <form id="auth-form" ref={authFormRef} onSubmit={saveAuth}>
          <input
            name="name"
            placeholder="Auth name"
            required
            value={authFormData.name}
            onChange={(e) => setAuthFormData((prev) => ({ ...prev, name: e.target.value }))}
          />
          <div className="row">
            <select
              name="provider"
              value={authFormData.provider}
              onChange={(e) =>
                setAuthFormData((prev) => ({ ...prev, provider: e.target.value as ProviderKey }))
              }
            >
              <option value="codex">Codex</option>
              <option value="claude">Claude Code</option>
              <option value="junie">Junie</option>
            </select>
            {authHasSubscription ? (
              <select
                name="auth_mode"
                value={authMode}
                onChange={(e) => {
                  const next = e.target.value as AuthMode;
                  if (next === 'subscription') {
                    setAuthFormData((prev) => ({ ...prev, auth_type: 'login_bundle' }));
                    setSubscriptionVariant('automatic');
                  } else {
                    setAuthFormData((prev) => ({ ...prev, auth_type: 'api_key' }));
                  }
                }}
              >
                <option value="api">API-based</option>
                <option value="subscription">Subscription-based</option>
              </select>
            ) : (
              <select
                name="auth_type"
                value={authFormData.auth_type}
                onChange={(e) =>
                  setAuthFormData((prev) => ({ ...prev, auth_type: e.target.value as AuthType }))
                }
              >
                {authTypesForProvider(authFormData.provider).map((value) => (
                  <option key={value} value={value}>
                    {authTypeLabel(value, authFormData.provider)}
                  </option>
                ))}
              </select>
            )}
          </div>
          <p className="status">
            Existing auth entries remain supported for compatibility. New Codex/Claude CLI sessions
            should use AI Fence managed credentials when available.
          </p>

          {/* Global toggle for admins */}
          {isAdmin && (
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={authFormData.is_global}
                onChange={(e) =>
                  setAuthFormData((prev) => ({ ...prev, is_global: e.target.checked }))
                }
              />
              <span>Global auth (available to all users)</span>
            </label>
          )}

          {renderAuthFields()}
        </form>
      </Dialog>

      {/* Auth entries list */}
      <div className="card full">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Auth entries</h2>
          <button type="button" onClick={openCreateAuthDialog}>
            + Create Auth
          </button>
        </div>
        <p className="status">
          Obsolete auth entries are kept for backwards compatibility. Prefer AI Fence managed gateway
          credentials for new Codex/Claude runs.
        </p>
        <div className="model-settings-table-scroll" role="region" aria-label="Auth entries table" tabIndex={0}>
          <table aria-label="Auth entries">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Provider</th>
                <th scope="col">Type</th>
                <th scope="col">Usage</th>
                <th scope="col">Scope</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {auths.map((auth) => (
                <tr key={auth.id}>
                  <td>{auth.name}</td>
                  <td>{auth.provider ?? 'any'}</td>
                  <td>
                    <div>{authTypeLabel(auth.auth_type, auth.provider ?? undefined)}</div>
                    {legacyAuthHint(auth.auth_type, auth.provider ?? undefined) && (
                      <div className="hint">
                        {legacyAuthHint(auth.auth_type, auth.provider ?? undefined)}
                      </div>
                    )}
                  </td>
                  <td title={usageTitleForAuth(auth)}>
                    {usageSummaryForAuth(auth)}
                  </td>
                  <td>{auth.is_global ? 'Global' : 'User'}</td>
                  <td className="row">
                    <button aria-label={`Edit: ${auth.name}`} onClick={() => startEditingAuth(auth)}>Edit</button>
                    {isAdmin && (
                      <button onClick={() => toggleAuthGlobal(auth)}>
                        {auth.is_global ? 'Make user' : 'Make global'}
                      </button>
                    )}
                    <button aria-label={`Delete: ${auth.name}`} onClick={() => deleteAuth(auth)}>Delete</button>
                  </td>
                </tr>
              ))}
              {auths.length === 0 && (
                <tr>
                  <td colSpan={6}>No auth entries yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Usage Provider Dialog */}
      <Dialog
        title={editingUsageProviderId ? 'Edit Usage Provider' : 'Create Usage Provider'}
        open={usageProviderDialogOpen}
        onClose={resetUsageProviderForm}
        size="medium"
        footer={
          <div className="row" style={{ justifyContent: 'flex-end', width: '100%' }}>
            <button type="button" className="secondary" onClick={resetUsageProviderForm}>
              Cancel
            </button>
            <button type="submit" form="usage-provider-form" disabled={usageProviderBusy}>
              {usageProviderBusy ? 'Saving…' : editingUsageProviderId ? 'Update' : 'Create'}
            </button>
          </div>
        }
      >
        {modelError && <ErrorBanner message={modelError} onDismiss={() => setModelError(null)} />}
        <form id="usage-provider-form" onSubmit={saveUsageProvider}>
          <input
            name="name"
            placeholder="Usage provider name"
            required
            value={usageProviderFormData.name}
            onChange={(e) => setUsageProviderFormData((prev) => ({ ...prev, name: e.target.value }))}
          />
          <div className="row">
            <select
              name="provider"
              value={usageProviderFormData.provider}
              onChange={(e) =>
                setUsageProviderFormData((prev) => ({ ...prev, provider: e.target.value as UsageProviderKind }))
              }
            >
              <option value="kimi_code">Kimi Code</option>
              <option value="openrouter">OpenRouter</option>
              <option value="minimax">MiniMax</option>
              <option value="zai">ZAI (Zhipu)</option>
              <option value="codex">Codex</option>
              <option value="claude">Claude</option>
              <option value="custom">Custom</option>
            </select>
            <select
              name="linked_auth_id"
              value={usageProviderFormData.linked_auth_id}
              onChange={(e) =>
                setUsageProviderFormData((prev) => ({ ...prev, linked_auth_id: e.target.value }))
              }
              required
            >
              <option value="">Select linked auth...</option>
              {auths.map((auth) => (
                <option key={auth.id} value={auth.id}>
                  {auth.name} ({auth.provider ?? 'any'})
                </option>
              ))}
            </select>
          </div>

          <textarea
            name="config_json"
            placeholder="Config JSON (optional)"
            value={usageProviderFormData.config_json}
            onChange={(e) =>
              setUsageProviderFormData((prev) => ({ ...prev, config_json: e.target.value }))
            }
            rows={4}
          />

          {/* Global toggle for admins */}
          {isAdmin && (
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={usageProviderFormData.is_global}
                onChange={(e) =>
                  setUsageProviderFormData((prev) => ({ ...prev, is_global: e.target.checked }))
                }
              />
              <span>Global usage provider (available to all users)</span>
            </label>
          )}
        </form>
      </Dialog>

      {/* Usage Providers list */}
      <div className="card full">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Usage Providers</h2>
          <button type="button" onClick={openCreateUsageProviderDialog}>
            + Create Usage Provider
          </button>
        </div>
        <div className="model-settings-table-scroll" role="region" aria-label="Usage Providers table" tabIndex={0}>
          <table aria-label="Usage Providers">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Provider</th>
                <th scope="col">Linked Auth</th>
                <th scope="col">Usage</th>
                <th scope="col">Scope</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {usageProviders.map((provider) => (
                <tr key={provider.id}>
                  <td>{provider.name}</td>
                  <td>{provider.provider}</td>
                  <td>{auths.find(a => a.id === provider.linked_auth_id)?.name ?? provider.linked_auth_id ?? 'None'}</td>
                  <td title={usageCellTitle(provider)}>
                    <div className="usage-cell">
                      <span className={`usage-status usage-status--${usageStatusForProvider(provider)}`}>
                        {usageStatusLabel(provider)}
                      </span>
                      <span>{formatUsageDisplay(provider)}</span>
                    </div>
                  </td>
                  <td>{provider.is_global ? 'Global' : 'User'}</td>
                  <td className="row">
                    <button aria-label={`Edit: ${provider.name}`} onClick={() => startEditingUsageProvider(provider)}>Edit</button>
                    <button aria-label={`Test: ${provider.name}`} onClick={() => testUsageProvider(provider)}>Test</button>
                    {isAdmin && (
                      <button onClick={() => toggleUsageProviderGlobal(provider)}>
                        {provider.is_global ? 'Make user' : 'Make global'}
                      </button>
                    )}
                    <button aria-label={`Delete: ${provider.name}`} onClick={() => deleteUsageProvider(provider)}>Delete</button>
                  </td>
                </tr>
              ))}
              {usageProviders.length === 0 && (
                <tr>
                  <td colSpan={6}>No usage providers yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Model Dialog */}
      <Dialog
        title={editingModelId ? 'Edit Tool Profile' : 'Create Tool Profile'}
        open={modelDialogOpen}
        onClose={resetModelForm}
        size="medium"
        footer={
          <div className="row" style={{ justifyContent: 'flex-end', width: '100%' }}>
            <button type="button" className="secondary" onClick={resetModelForm}>
              Cancel
            </button>
            <button type="submit" form="model-form">
              {editingModelId ? 'Update profile' : 'Create profile'}
            </button>
          </div>
        }
      >
        <form id="model-form" onSubmit={saveModel}>
          <div className="tabs" role="tablist" aria-label="Model profile configuration">
            <button
              type="button"
              className={modelDialogTab === 'basic' ? 'active' : ''}
              onClick={() => setModelDialogTab('basic')}
            >
              Basic
            </button>
            <button
              type="button"
              className={modelDialogTab === 'advanced' ? 'active' : ''}
              onClick={() => setModelDialogTab('advanced')}
            >
              Advanced
            </button>
          </div>

          {modelDialogTab === 'basic' ? (
            <>
              <input
                name="name"
                placeholder="Profile name"
                required
                value={modelFormData.name}
                onChange={(e) => setModelFormData((prev) => ({ ...prev, name: e.target.value }))}
              />
              <select
                name="provider"
                value={modelFormData.provider}
                onChange={(e) => setModelFormData((prev) => ({ ...prev, provider: e.target.value as ProviderKey }))}
              >
                <option value="codex">Codex (cloud)</option>
                <option value="claude">Claude Code CLI</option>
                <option value="junie">Junie CLI</option>
                <option value="oss">Local provider (--oss)</option>
                <option value="openai-compatible">OpenAI-compatible</option>
              </select>
              {modelFormData.provider !== 'claude' ? (
                <input
                  name="model"
                  placeholder="Model (e.g. gpt-5.2-codex)"
                  value={modelFormData.model}
                  onChange={(e) => setModelFormData((prev) => ({ ...prev, model: e.target.value }))}
                />
              ) : (
                <>
                  <p className="status">Configure default Anthropic models for this profile:</p>
                  <input
                    name="default_haiku_model"
                    placeholder="Default Haiku model (e.g. claude-3-haiku-20240307)"
                    value={modelFormData.default_haiku_model}
                    onChange={(e) =>
                      setModelFormData((prev) => ({ ...prev, default_haiku_model: e.target.value }))
                    }
                  />
                  <input
                    name="default_sonnet_model"
                    placeholder="Default Sonnet model (e.g. claude-3-sonnet-20240229)"
                    value={modelFormData.default_sonnet_model}
                    onChange={(e) =>
                      setModelFormData((prev) => ({ ...prev, default_sonnet_model: e.target.value }))
                    }
                  />
                  <input
                    name="default_opus_model"
                    placeholder="Default Opus model (e.g. claude-3-opus-20240229)"
                    value={modelFormData.default_opus_model}
                    onChange={(e) =>
                      setModelFormData((prev) => ({ ...prev, default_opus_model: e.target.value }))
                    }
                  />
                  <input
                    name="api_timeout_ms"
                    placeholder="API timeout (ms)"
                    value={modelFormData.api_timeout_ms}
                    onChange={(e) =>
                      setModelFormData((prev) => ({ ...prev, api_timeout_ms: e.target.value }))
                    }
                  />
                </>
              )}
              {modelFormData.provider === 'oss' && (
                <input
                  name="local_provider"
                  placeholder="Local provider (ollama/lmstudio)"
                  value={modelFormData.local_provider}
                  onChange={(e) =>
                    setModelFormData((prev) => ({ ...prev, local_provider: e.target.value }))
                  }
                />
              )}
              <select
                name="auth_id"
                value={modelFormData.auth_id}
                onChange={(e) => setModelFormData((prev) => ({ ...prev, auth_id: e.target.value }))}
              >
                <option value="">Auth (optional)</option>
                {auths
                  .filter((auth) =>
                    providerAllowsAuth(modelFormData.provider, auth.auth_type, auth.provider)
                  )
                  .map((auth) => (
                    <option key={auth.id} value={auth.id}>
                      {auth.name} ({authTypeLabel(auth.auth_type, modelFormData.provider)})
                    </option>
                  ))}
              </select>
              <select
                name="usage_provider_id"
                value={modelFormData.usage_provider_id}
                onChange={(e) =>
                  setModelFormData((prev) => ({ ...prev, usage_provider_id: e.target.value }))
                }
              >
                <option value="">Usage Provider (optional)</option>
                {usageProviders.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name} ({provider.provider})
                  </option>
                ))}
              </select>
            </>
          ) : (
            <>
              {isCodexLikeProvider(modelFormData.provider) && (
                <>
                  <label className="checkbox-row">
                    <span>Thinking level</span>
                    <select
                      name="model_reasoning_effort"
                      value={modelFormData.model_reasoning_effort}
                      onChange={(e) =>
                        setModelFormData((prev) => ({
                          ...prev,
                          model_reasoning_effort: e.target.value
                        }))
                      }
                    >
                      <option value="">Not set (default)</option>
                      <option value="minimal">minimal</option>
                      <option value="low">low</option>
                      <option value="medium">medium</option>
                      <option value="high">high</option>
                      <option value="xhigh">xhigh</option>
                    </select>
                  </label>
                  <label className="checkbox-row">
                    <span>Auto-compact</span>
                    <select
                      name="model_auto_compact_mode"
                      value={modelFormData.model_auto_compact_mode}
                      onChange={(e) =>
                        setModelFormData((prev) => ({
                          ...prev,
                          model_auto_compact_mode: e.target.value
                        }))
                      }
                    >
                      <option value="codex_default">Codex default</option>
                      <option value="custom">Custom token limit</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </label>
                  {modelFormData.model_auto_compact_mode === 'custom' && (
                    <input
                      name="model_auto_compact_token_limit"
                      inputMode="numeric"
                      placeholder="Auto-compact token limit"
                      value={modelFormData.model_auto_compact_token_limit}
                      onChange={(e) =>
                        setModelFormData((prev) => ({
                          ...prev,
                          model_auto_compact_token_limit: e.target.value
                        }))
                      }
                    />
                  )}
                </>
              )}
              <label className="checkbox-row">
                <span>Turn supervision token limit</span>
                <input
                  name="model_turn_supervision_tokens"
                  inputMode="numeric"
                  placeholder="Limit for this profile (empty = unlimited)"
                  value={modelFormData.model_turn_supervision_tokens}
                  onChange={(e) =>
                    setModelFormData((prev) => ({
                      ...prev,
                      model_turn_supervision_tokens: e.target.value
                    }))
                  }
                />
              </label>
            </>
          )}

          <label className="checkbox-row">
            <input
              type="checkbox"
              name="is_default"
              checked={modelFormData.is_default}
              onChange={(e) => setModelFormData((prev) => ({ ...prev, is_default: e.target.checked }))}
            />
            <span>Set as default</span>
          </label>

          {/* Global toggle for admins */}
          {isAdmin && (
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={modelFormData.is_global}
                onChange={(e) => setModelFormData((prev) => ({ ...prev, is_global: e.target.checked }))}
              />
              <span>Global profile (available to all users)</span>
            </label>
          )}
        </form>
      </Dialog>

      {/* AI Fence Catalog */}
      <div className="card full">
        <FenceLinkedSources apiClient={apiClient} onImported={loadModels} />
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h2>AI Fence Catalog</h2>
          <button type="button" className="secondary" onClick={loadAiFenceModels} disabled={aiFenceBusy}>
            {aiFenceBusy ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <label htmlFor="model-settings-catalog-filter">Filter AI Fence catalog</label>
        <div className="row" style={{ alignItems: 'center', gap: 12 }}>
          <input
            id="model-settings-catalog-filter"
            placeholder="Filter models"
            value={aiFenceQuery}
            onChange={(event) => setAiFenceQuery(event.target.value)}
            style={{ maxWidth: 360 }}
          />
          <span className="status" role="status">
            {aiFenceBusy ? 'Loading catalog…' : aiFenceFailed ? 'Catalog unavailable' : aiFenceMatchCount === aiFenceModels.length
              ? `${aiFenceModels.length} available`
              : `${aiFenceMatchCount} matches`}
          </span>
        </div>
        <div className="model-settings-table-scroll" role="region" aria-label="AI Fence Catalog table" tabIndex={0}>
          <table aria-label="AI Fence Catalog">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Provider</th>
                <th scope="col">Model</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAiFenceModels.map((item) => {
                const enabledModel = item.enabled_model_id
                  ? models.find((model) => model.id === item.enabled_model_id)
                  : null;
                return (
                  <tr key={`${item.provider}:${item.model}`}>
                    <td>{item.name}</td>
                    <td>{item.provider}</td>
                    <td>{item.model}</td>
                    <td>
                      {item.enabled_profile_count > 0
                        ? `${item.enabled_profile_count} profile${item.enabled_profile_count === 1 ? '' : 's'}`
                        : 'Disabled'}
                    </td>
                    <td className="row">
                      {enabledModel && (
                        <button type="button" onClick={() => startEditingModel(enabledModel)}>
                          Edit first
                        </button>
                      )}
                      <button type="button" onClick={() => createAiFenceModelProfile(item)}>
                        {item.enabled_profile_count > 0 ? 'Create variant' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              <CatalogFeedback
                busy={aiFenceBusy}
                failed={aiFenceFailed}
                total={aiFenceModels.length}
                matches={filteredAiFenceModels.length}
                onRetry={loadAiFenceModels}
                onClear={() => setAiFenceQuery('')}
              />
              {aiFenceMatchCount > filteredAiFenceModels.length && (
                <tr>
                  <td colSpan={5}>Showing first {filteredAiFenceModels.length} matches.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Configured Tool Profiles */}
      <div className="card full">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Configured Tool Profiles</h2>
          <button type="button" onClick={openCreateModelDialog}>
            + Create Profile
          </button>
        </div>
        <div className="model-settings-table-scroll" role="region" aria-label="Configured Tool Profiles table" tabIndex={0}>
          <table aria-label="Configured Tool Profiles">
            <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Provider</th>
                  <th scope="col">Model/Settings</th>
                  <th scope="col">Auth</th>
                  <th scope="col">Usage</th>
                  <th scope="col">Scope</th>
                  <th scope="col">Default</th>
                  <th scope="col">Supervision</th>
                  <th scope="col">Actions</th>
                </tr>
            </thead>
            <tbody>
              {models.map((model) => (
                <tr key={model.id}>
                  <td>{model.name}</td>
                  <td>{model.provider}</td>
                  <td>
                    {model.provider === 'claude'
                      ? `Haiku: ${model.default_haiku_model ?? 'default'}, Sonnet: ${model.default_sonnet_model ?? 'default'}, Opus: ${model.default_opus_model ?? 'default'}`
                      : [
                          model.model ?? '-',
                          model.model_reasoning_effort ? `thinking: ${model.model_reasoning_effort}` : null,
                          formatAutoCompact(model)
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                  </td>
                  <td>{auths.find((auth) => auth.id === model.auth_id)?.name ?? '-'}</td>
                  <td>
                    {(() => {
                      const provider = usageProviders.find((p) => p.id === model.usage_provider_id);
                      if (!provider) return '-';
                      const status = usageStatusLabel(provider);
                      return `${provider.name}: ${formatUsageDisplay(provider)} (${status})`;
                    })()}
                  </td>
                  <td>{model.is_global ? 'Global' : 'User'}</td>
                  <td>{model.is_default ? 'Yes' : 'No'}</td>
                  <td>{resolveSupervisionSettingLabel(model, supervisionModelTurnTokens)}</td>
                  <td className="row">
                    <button aria-label={`Edit: ${model.name}`} onClick={() => startEditingModel(model)}>Edit</button>
                    {!model.is_default && (
                      <button aria-label={`Make default: ${model.name}`} onClick={() => setDefault(model)}>Make default</button>
                    )}
                    {isAdmin && (
                      <button onClick={() => toggleModelGlobal(model)}>
                        {model.is_global ? 'Make user' : 'Make global'}
                      </button>
                    )}
                    <button aria-label={`Delete: ${model.name}`} onClick={() => deleteModel(model)}>Delete</button>
                  </td>
                </tr>
              ))}
              {models.length === 0 && (
                <tr>
                  <td colSpan={9}>No models configured.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

async function zipFiles(files: File[]) {
  const zip = new JSZip();
  files.forEach((file) => {
    const path = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    zip.file(path, file);
  });
  return zip.generateAsync({ type: 'base64' });
}
